import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { bridge } from '~/services/bridge'
import type { PluginPlatform } from '~/services/bridge'
import { getPluginAccessToken, setPluginAccessToken } from '~/services/bridge/pluginAuth'
import { shell } from '~/services/bridge/thinShellClient'

interface PlatformContextValue {
  isPlugin: boolean
  platform: PluginPlatform
  bridge: typeof bridge
  authVersion: number
  ready: boolean
}

const PlatformContext = createContext<PlatformContextValue>({
  isPlugin: false,
  platform: 'browser',
  bridge,
  authVersion: 0,
  ready: true
})

export function PlatformProvider({ children }: { children: ReactNode }) {
  const [authVersion, setAuthVersion] = useState(0)
  const isPlugin = bridge.isInPlugin()
  const [ready, setReady] = useState(!isPlugin)

  const value = useMemo(
    () => ({
      isPlugin,
      platform: bridge.getPlatform(),
      bridge,
      authVersion,
      ready
    }),
    [isPlugin, authVersion, ready]
  )

  useEffect(() => {
    if (!isPlugin) return

    let cancelled = false
    async function recoverToken() {
      try {
        if (!getPluginAccessToken()) {
          const token = await shell.getStorageItem('curietech_access_token')
          if (!cancelled && token) {
            setPluginAccessToken(token)
          }
        }
      } catch {
        // Best-effort recovery
      } finally {
        if (!cancelled) setReady(true)
      }
    }
    void recoverToken()
    return () => {
      cancelled = true
    }
  }, [isPlugin])

  useEffect(() => {
    if (!isPlugin) return

    const handleAuthCallback = (...args: unknown[]) => {
      const data = args[0] as { token?: string } | undefined
      if (data?.token) {
        setPluginAccessToken(data.token)
        setAuthVersion(v => v + 1)
      }
    }

    const handleAuthToken = (...args: unknown[]) => {
      const data = args[0] as { token?: string } | undefined
      if (data?.token) {
        setPluginAccessToken(data.token)
        setAuthVersion(v => v + 1)
      }
    }

    bridge.on('authCallback', handleAuthCallback)
    bridge.on('authToken', handleAuthToken)

    return () => {
      bridge.off('authCallback', handleAuthCallback)
      bridge.off('authToken', handleAuthToken)
    }
  }, [isPlugin])

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>
}

export function usePlatform() {
  return useContext(PlatformContext)
}
